import React, { useRef, useEffect, useLayoutEffect, useCallback, useState } from 'react';
import { IconButton } from './ui';
import { Filter } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useSearchStore } from '@/stores/searchStore';

interface SearchFiltersProps {
  forceClose?: boolean;
}

export default function SearchFilters({ forceClose }: SearchFiltersProps) {
  const filters = useSearchStore((s) => s.filters);
  const setFilters = useSearchStore((s) => s.setFilters);

  const roasterMode = Boolean(filters.sellsBeans && !filters.roasterOnly);

  const setToggle = useCallback(
    (key: keyof typeof filters) => (checked: boolean) => {
      setFilters({ [key]: checked } as any);
    },
    [setFilters]
  );

  const toggleRoasterMode = useCallback(
    (checked: boolean) => {
      // Mixed scope = roasters OR shops with beans
      // When turning OFF, clear subfilters ONCE here (no effects)
      setFilters({
        sellsBeans: checked,
        roastsOrSellsBeans: checked,
        roasterOnly: false,
        ...(checked ? {} : { organic: false, givesBack: false }),
      } as any);
    },
    [setFilters]
  );

  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  // Real position, measured against the panel's own rendered size — not
  // assumed. The inline style below only ever used a hardcoded 300px guess
  // computed before the panel existed in the DOM, which is exactly the kind
  // of thing that quietly stops matching reality on a different viewport.
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

  const location = useLocation();

  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const paperRef = useRef<HTMLDivElement | null>(null);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleIconClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(e.currentTarget);
    setOpen((prev) => !prev);
  }, []);

  // Measure and clamp against the panel's actual rendered box, after it's in
  // the DOM — not a hardcoded 300px guess computed before it existed. Runs
  // via useLayoutEffect so the correction happens before the browser paints,
  // and re-runs on resize/orientation change (covers a mobile browser's
  // toolbar showing/hiding, which changes the usable viewport after open).
  useLayoutEffect(() => {
    if (!open || !anchorEl) {
      setPosition(null);
      return;
    }
    const measure = () => {
      const panel = paperRef.current;
      if (!panel) return;
      const anchorRect = anchorEl.getBoundingClientRect();
      const panelWidth = panel.offsetWidth;
      const viewportWidth = document.documentElement.clientWidth;
      const margin = 8;
      const left = Math.min(
        Math.max(anchorRect.left, margin),
        viewportWidth - panelWidth - margin
      );
      setPosition({ top: anchorRect.bottom, left });
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [open, anchorEl]);

  // Close on Escape even if focus is inside switches
  useEffect(() => {
    if (!open) return;
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // Close when route changes
  useEffect(() => {
    setOpen(false);
    setAnchorEl(null);
  }, [location.pathname]);

  // Allow parent components to force-close the popover
  useEffect(() => {
    if (forceClose) {
      setOpen(false);
      setAnchorEl(null);
    }
  }, [forceClose]);

  // Attach/detach global click-away listeners only while open
  useEffect(() => {
    if (!open) return;
    const handleClickAway = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;
      if (paperRef.current?.contains(target)) return;
      if (buttonRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handleClickAway);
    document.addEventListener('touchstart', handleClickAway, { passive: true } as AddEventListenerOptions);
    return () => {
      document.removeEventListener('mousedown', handleClickAway);
      document.removeEventListener('touchstart', handleClickAway);
    };
  }, [open]);

  const popoverId = open ? 'filters-popover' : undefined;

  return (
    <>
      <IconButton
        id="filters-button"
        aria-describedby={popoverId}
        aria-haspopup="dialog"
        aria-label="Open search filters"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleIconClick}
        ref={buttonRef}
      >
        <Filter className="w-6 h-6" />
      </IconButton>

      {open && (
        <div className="fixed inset-0 z-40" onClick={handleClose} />
      )}
      
      {open && (
        <div
          id={popoverId}
          ref={paperRef}
          className="fixed z-50 bg-card-bg rounded-lg shadow-xl p-2 w-[300px] max-w-[90vw] mt-2"
          style={
            position
              ? { top: position.top, left: position.left, visibility: 'visible' }
              : { top: 0, left: 0, visibility: 'hidden' }
          }
          onKeyDown={(e) => {
            if (e.key === 'Escape') handleClose();
          }}
        >
          <div
            className="w-[260px] p-2"
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            data-testid="filters-list"
          >
          {/* Independent */}
          <label className="flex items-center gap-3 px-2 py-2 cursor-pointer hover:bg-hover-bg rounded">
            <input
              type="checkbox"
              checked={!!filters.independent}
              onChange={(e) => setToggle('independent')(e.target.checked)}
              className="w-5 h-5 rounded border-card-border text-primary focus:ring-primary focus:ring-offset-gray-800"
            />
            <div className="text-sm font-medium text-text-primary">Independent only</div>
          </label>

          {/* Dog Friendly */}
          <label className="flex items-center gap-3 px-2 py-2 cursor-pointer hover:bg-hover-bg rounded">
            <input
              type="checkbox"
              checked={!!filters.dogFriendly}
              onChange={(e) => setToggle('dogFriendly')(e.target.checked)}
              className="w-5 h-5 rounded border-card-border text-primary focus:ring-primary focus:ring-offset-gray-800"
            />
            <div className="text-sm font-medium text-text-primary">Dog-friendly patio</div>
          </label>

          {/* Farmers Market */}
          <label className="flex items-center gap-3 px-2 py-2 cursor-pointer hover:bg-hover-bg rounded">
            <input
              type="checkbox"
              checked={!!filters.farmersMarket}
              onChange={(e) => setToggle('farmersMarket')(e.target.checked)}
              className="w-5 h-5 rounded border-card-border text-primary focus:ring-primary focus:ring-offset-gray-800"
            />
            <div className="text-sm font-medium text-text-primary">Farmers market</div>
          </label>

          {/* Roaster / Sells Beans (mixed scope) */}
          <label className="flex items-center gap-3 px-2 py-2 cursor-pointer hover:bg-hover-bg rounded">
            <input
              type="checkbox"
              checked={roasterMode}
              onChange={(e) => toggleRoasterMode(e.target.checked)}
              className="w-5 h-5 rounded border-card-border text-primary focus:ring-primary focus:ring-offset-gray-800"
            />
            <div className="text-sm font-medium text-text-primary">Roaster / Sells Beans</div>
          </label>

          {/* Nested roaster refinements */}
          {roasterMode && (
            <>
              <div className="border-t border-card-border my-2" />
              <div className="pl-4">
                {/* Organic */}
                <label className="flex items-center gap-3 px-2 py-2 cursor-pointer hover:bg-hover-bg rounded">
                  <input
                    type="checkbox"
                    checked={!!filters.organic}
                    onChange={(e) => setToggle('organic')(e.target.checked)}
                    className="w-5 h-5 rounded border-card-border text-primary focus:ring-primary focus:ring-offset-gray-800"
                  />
                  <div className="text-sm font-medium text-text-primary">Organic</div>
                </label>

                {/* Gives back */}
                <label className="flex items-center gap-3 px-2 py-2 cursor-pointer hover:bg-hover-bg rounded">
                  <input
                    type="checkbox"
                    checked={!!filters.givesBack}
                    onChange={(e) => setToggle('givesBack')(e.target.checked)}
                    className="w-5 h-5 rounded border-card-border text-primary focus:ring-primary focus:ring-offset-gray-800"
                  />
                  <div className="text-sm font-medium text-text-primary">Gives back</div>
                </label>
              </div>
            </>
          )}
          </div>
        </div>
      )}
    </>
  );
}

